import { PropsWithChildren, useMemo } from 'react';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { fetchTickets } from 'api/tickets';
import { FilterProvider } from 'components/filter/filters-next/FilterProvider';
import { useFiltersByKey } from 'components/filter/filters-next/useFiltersByKey';
import * as Table from 'components/table';
import { ColumnFiltersFeature } from 'components/table/ColumnFiltersFeature';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { useCoreTable } from 'components/table/useCoreTable';
import { useCoreTableColumns } from 'components/table/useCoreTableColumns';
import { useCoreTableProps } from 'components/table/useCoreTableProps';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { isEmpty } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';

const TicketsTableProvider = (props: PropsWithChildren) => {
    const { children } = props;
    const {
        filterCacheKey = 'tickets',
        enableSchema = false,
        options = {},
        coreTableOptions = {},
    } = useCoreTableProps<'tickets'>();
    const { meta, ...restOptions } = options;
    const { queryOptions = {}, tableStateOptions = {} } = coreTableOptions;
    const { props: tableProps } = tableStateOptions;
    const { filters } = useFiltersByKey(filterCacheKey);
    const { accountId } = useCurrentAccount();
    const { isPending, params, query, searchState, tableState } = useCoreTable('tickets', {
        queryOptions: {
            queryKey: accountQueryKeys.ticketsWithFilters,
            queryFn: fetchTickets,
            ...queryOptions,
        },
        tableStateOptions: {
            params: {
                eager: true,
            },
            ...tableStateOptions,
        },
    });

    const columns = useCoreTableColumns('tickets');

    const { data } = query;

    const ctx = useMemo(
        () => ({
            query,
            params,
            enableSchema,
            isPending,
            ...searchState,
        }),
        [query, params, enableSchema, isPending, searchState],
    );

    const {
        setPagination,
        setExpanded,
        setSorting,
        setColumnVisibility,
        setColumnPinning,
        setColumnOrder,
        setGivebutterColumnFilters,
        setIsFilterBarExpanded,
    } = tableState;

    const table = useReactTable({
        _features: [ColumnFiltersFeature],
        data: data.data,
        columns,
        pageCount: data?.meta.last_page ?? -1,
        rowCount: data.meta.total,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        initialState: tableState,
        state: tableState,
        onPaginationChange: setPagination,
        onExpandedChange: setExpanded,
        onSortingChange: setSorting,
        onColumnOrderChange: setColumnOrder,
        onColumnPinningChange: setColumnPinning,
        onColumnVisibilityChange: setColumnVisibility,
        onGivebutterColumnFiltersChange: setGivebutterColumnFilters,
        onIsFilterBarExpandedChange: setIsFilterBarExpanded,
        manualPagination: true,
        manualSorting: true,
        meta: {
            enableColumnOrder: true,
            to: cell => `/accounts/${accountId}/tickets/${cell.row.original.id}`,
            ...meta,
        },
        ...restOptions,
    });

    useSyncPaginatedData(table, data);

    return (
        <FilterProvider cacheKey={filterCacheKey} resource={'tickets'}>
            <Table.Root
                isLoading={isPending}
                table={table}
                searchTerm={params.query}
                variant="bordered"
                isFiltered={!isEmpty(filters)}
                {...tableProps}
            >
                <TableCoreStateContext.Provider value={ctx}>
                    {children}
                </TableCoreStateContext.Provider>
            </Table.Root>
        </FilterProvider>
    );
};

export { TicketsTableProvider };
